// src/components/workspace/IncidentReportView.js
import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { ShieldCheck, CheckCircle2, Download } from "lucide-react";
import { formatDuration } from "../../utils/duration";

const STATUS_BADGE = {
  resolved: {
    label: "Fully resolved",
    className: "bg-emerald-100 text-emerald-800 border-emerald-300",
    htmlClass: "resolved",
  },
  partially_resolved: {
    label: "Partially resolved",
    className: "bg-amber-100 text-amber-800 border-amber-300",
    htmlClass: "partially_resolved",
  },
  unresolved: {
    label: "Could not resolve",
    className: "bg-rose-100 text-rose-800 border-rose-300",
    htmlClass: "unresolved",
  },
};

function formatTs(ts) {
  if (!ts?.seconds) return "—";
  return new Date(ts.seconds * 1000).toLocaleString();
}

function buildDownloadHtml(report) {
  const badge = STATUS_BADGE[report.resolutionStatus] || STATUS_BADGE.resolved;
  const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const nl2br = (s) => esc(s).replace(/\n/g, "<br>");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Incident Resolution Report — ${esc(report.reportId)}</title>
  <style>
    body { font-family: Arial, Helvetica, sans-serif; max-width: 720px; margin: 40px auto; color: #1e293b; line-height: 1.6; font-size: 14px; }
    h1 { font-size: 22px; margin: 0 0 4px; }
    .mono { font-family: monospace; color: #64748b; font-size: 12px; }
    .badge { display: inline-block; padding: 4px 14px; border-radius: 999px; font-size: 13px; font-weight: 700; margin-top: 10px; }
    .resolved { background: #dcfce7; color: #166534; }
    .partially_resolved { background: #fef3c7; color: #92400e; }
    .unresolved { background: #fee2e2; color: #991b1b; }
    section { margin-top: 22px; padding-top: 18px; border-top: 1px solid #e2e8f0; }
    h4 { font-size: 14px; font-weight: 700; margin: 0 0 8px; color: #111827; }
    p { margin: 0; color: #374151; }
    ul { list-style: none; padding: 0; margin: 0; }
    li { padding: 3px 0; }
    li::before { content: "✓  "; color: #16a34a; font-weight: 700; }
    .warranty { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 14px 18px; margin-top: 22px; }
    .warranty h4 { color: #1e3a8a; }
    .warranty p { color: #1e40af; }
    .footer { margin-top: 28px; padding-top: 16px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
    @media print { body { margin: 20px; } }
  </style>
</head>
<body>
  <h1>Incident Resolution Report</h1>
  <p class="mono">${esc(report.reportId)}</p>
  <span class="badge ${badge.htmlClass}">${badge.label}</span>

  <section>
    <h4>Root Cause Identified</h4>
    <p>${nl2br(report.rootCause)}</p>
  </section>

  <section>
    <h4>What Was Fixed</h4>
    <p>${nl2br(report.actionsTaken)}</p>
  </section>

  <section>
    <h4>Verification Completed</h4>
    <ul>${(report.verificationSteps || []).map((s) => `<li>${esc(s)}</li>`).join("")}</ul>
  </section>

  ${report.preventionNotes ? `
  <section>
    <h4>How to Prevent This in Future</h4>
    <p>${nl2br(report.preventionNotes)}</p>
  </section>` : ""}

  <div class="warranty">
    <h4>Warranty</h4>
    <p>If the same issue recurs within 48 hours, contact us for free re-engagement.<br>
    Warranty expires: <strong>${formatTs(report.warrantyExpiresAt)}</strong></p>
  </div>

  ${report.metrics ? `
  <section>
    <h4>Resolution metrics</h4>
    <p>Time to first response: <strong>${esc(report.metrics.timeToFirstResponseLabel || "—")}</strong><br>
    Time to resolution: <strong>${esc(report.metrics.timeToResolveLabel || "—")}</strong><br>
    Live session time: <strong>${esc(report.metrics.sessionLabel || "—")}</strong>
    ${report.metrics.sessionCount ? ` (${report.metrics.sessionCount} session${report.metrics.sessionCount === 1 ? "" : "s"})` : ""}</p>
  </section>` : ""}

  <div class="footer">
    <p>Filed: ${formatTs(report.createdAt)} &nbsp;·&nbsp; Lane: ${esc(report.lane)}${report.sessionRecordingConsent ? " &nbsp;·&nbsp; Recording consent: yes" : ""}</p>
    <p style="margin-top:8px;color:#94a3b8;">A refund applies only when the reported incident remains unresolved and was within scope.</p>
    <p style="margin-top:12px;font-weight:700;color:#374151;">SkillSyncs Intelligence Team &nbsp;—&nbsp; www.skillsyncs.com</p>
  </div>
</body>
</html>`;
}

/**
 * Read-only Incident Resolution Report, visible to both the specialist and the
 * requester once the report has been filed. Includes a one-click HTML download.
 */
function IncidentReportView({ reportId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!reportId) {
      setLoading(false);
      return;
    }
    const unsub = onSnapshot(
      doc(db, "incidentReports", reportId),
      (snap) => {
        if (snap.exists()) {
          setReport({ id: snap.id, ...snap.data() });
          setError(null);
        } else {
          setError("Report not found.");
        }
        setLoading(false);
      },
      (err) => {
        console.error("Incident report read error:", err);
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [reportId]);

  const handleDownload = () => {
    if (!report) return;
    const html = buildDownloadHtml(report);
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${report.reportId || "incident-report"}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <p className="text-sm text-slate-500 py-6 text-center">
        {error || "No resolution report is available yet."}
      </p>
    );
  }

  const badge = STATUS_BADGE[report.resolutionStatus] || STATUS_BADGE.resolved;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-blue-600" />
          <div>
            <p className="text-base font-bold text-slate-900">
              Incident Resolution Report
            </p>
            <p className="text-xs font-mono text-slate-500">{report.reportId}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`px-3 py-1 text-xs font-semibold rounded-full border ${badge.className}`}
          >
            {badge.label}
          </span>
          <button
            onClick={handleDownload}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition text-xs font-medium border border-slate-200"
            title="Download report as HTML (printable as PDF)"
          >
            <Download className="w-3.5 h-3.5" />
            Download
          </button>
        </div>
      </div>

      {/* Root cause */}
      <section>
        <h4 className="text-sm font-semibold text-slate-900 mb-1">
          Root cause identified
        </h4>
        <p className="text-sm text-slate-700 whitespace-pre-wrap">
          {report.rootCause}
        </p>
      </section>

      {/* Actions taken */}
      <section>
        <h4 className="text-sm font-semibold text-slate-900 mb-1">
          What was fixed
        </h4>
        <p className="text-sm text-slate-700 whitespace-pre-wrap">
          {report.actionsTaken}
        </p>
      </section>

      {/* Verification */}
      <section>
        <h4 className="text-sm font-semibold text-slate-900 mb-2">
          Verification completed
        </h4>
        <ul className="space-y-1.5">
          {(report.verificationSteps || []).map((step) => (
            <li key={step} className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              {step}
            </li>
          ))}
        </ul>
      </section>

      {/* Prevention */}
      {report.preventionNotes && (
        <section>
          <h4 className="text-sm font-semibold text-slate-900 mb-1">
            How to prevent this in future
          </h4>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">
            {report.preventionNotes}
          </p>
        </section>
      )}

      {report.metrics && (
        <section className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <h4 className="text-sm font-semibold text-slate-900 mb-2">
            Resolution metrics
          </h4>
          <dl className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div>
              <dt className="text-2xs font-semibold uppercase tracking-wide text-slate-500">
                First response
              </dt>
              <dd className="text-sm font-semibold text-slate-800">
                {report.metrics.timeToFirstResponseLabel ||
                  formatDuration(report.metrics.timeToFirstResponseMs)}
              </dd>
            </div>
            <div>
              <dt className="text-2xs font-semibold uppercase tracking-wide text-slate-500">
                Time to resolve
              </dt>
              <dd className="text-sm font-semibold text-slate-800">
                {report.metrics.timeToResolveLabel ||
                  formatDuration(report.metrics.timeToResolveMs)}
              </dd>
            </div>
            <div>
              <dt className="text-2xs font-semibold uppercase tracking-wide text-slate-500">
                Live session
              </dt>
              <dd className="text-sm font-semibold text-slate-800">
                {report.metrics.sessionLabel ||
                  formatDuration(report.metrics.sessionMs)}
                {report.metrics.sessionCount
                  ? ` · ${report.metrics.sessionCount} session${
                      report.metrics.sessionCount === 1 ? "" : "s"
                    }`
                  : ""}
              </dd>
            </div>
          </dl>
        </section>
      )}

      {/* Warranty */}
      <section className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
        <h4 className="text-sm font-semibold text-blue-900 mb-1">Warranty</h4>
        <p className="text-sm text-blue-800">
          If the same issue recurs within 48 hours, contact us for free
          re-engagement. Warranty expires:{" "}
          <span className="font-semibold">
            {formatTs(report.warrantyExpiresAt)}
          </span>
        </p>
      </section>

      {/* Footer meta */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500 border-t border-slate-100 pt-3">
        <span>Filed: {formatTs(report.createdAt)}</span>
        <span>Lane: {report.lane}</span>
        {report.sessionRecordingConsent && <span>Recording consent: yes</span>}
      </div>

      <p className="text-xs text-slate-400">
        A refund applies only when the reported incident remains unresolved and
        was within scope.
      </p>
    </div>
  );
}

export default IncidentReportView;
